import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import PageWrap from "@/fileshare/components/PageWrap";
import api from "@/fileshare/lib/api";
import { formatApiErrorDetail } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Label } from "@/fileshare/components/ui/label";

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // If the user arrived from the reset-email link (?token=...), skip step 1.
  useEffect(() => {
    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
      setSent(true);
    }
  }, [tokenFromUrl]);

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Check your inbox — a reset link is on its way.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: newPassword });
      toast.success("Password reset! You can now log in.");
      setSent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap className="max-w-md mx-auto px-6 pt-20 pb-24">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Reset password</h1>
      <p className="text-sm text-slate-500 mt-1 mb-8">
        {tokenFromUrl
          ? "Almost done — choose a new password below."
          : "Enter your email and we'll send you a link to reset your password."}
      </p>

      {!sent ? (
        <form onSubmit={requestReset} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label className="text-sm text-slate-700">Email</Label>
            <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-slate-50/60 border-slate-200" />
          </div>
          <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11">
            {loading ? "Sending…" : "Send reset token"}
          </Button>
        </form>
      ) : (
        <form onSubmit={doReset} className="space-y-4" data-testid="reset-form">
          {!tokenFromUrl && (
            <div>
              <Label className="text-sm text-slate-700">Reset token</Label>
              <Input data-testid="reset-token" required value={resetToken} onChange={(e) => setResetToken(e.target.value)} placeholder="Paste token from the email" className="mt-1.5 bg-slate-50/60 border-slate-200" />
            </div>
          )}
          <div>
            <Label className="text-sm text-slate-700">New password</Label>
            <Input data-testid="reset-password" type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-1.5 bg-slate-50/60 border-slate-200" />
          </div>
          <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11">
            {loading ? "Resetting…" : "Reset password"}
          </Button>
        </form>
      )}

      <p className="text-sm text-slate-500 mt-6">
        <Link to="/fileshares/login" className="text-blue-500 font-medium hover:underline">Back to login</Link>
      </p>
    </PageWrap>
  );
}
